/**
 * DemocratCharts — API client.
 *
 * - Bearer token from auth store, attached to every request
 * - 401 → try /api/auth/refresh once, then retry the original request
 * - 401 on refresh → kick to /login
 */

import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { useAuthStore } from "@/store/auth";

export const api = axios.create({
  baseURL: "/api",
  withCredentials: true, // sends the refresh cookie
  timeout: 15_000,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh token mutex — don't fire 5 refresh calls when 5 parallel requests fail
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const resp = await axios.post(
        "/api/auth/refresh",
        {},
        { withCredentials: true, timeout: 10_000 }
      );
      const newToken = resp.data?.access_token as string | undefined;
      if (newToken) {
        useAuthStore.getState().setAccessToken(newToken);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Clear the mutex after a tick so concurrent callers see the resolution
      setTimeout(() => { refreshPromise = null; }, 0);
    }
  })();

  return refreshPromise;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Don't try to refresh on auth endpoints themselves — would infinite loop
    const isAuthCall = original?.url?.includes("/auth/login")
      || original?.url?.includes("/auth/refresh");

    if (error.response?.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(original);
      }
      // Refresh failed → logout
      useAuthStore.getState().clear();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);
